import { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Play, ImageIcon, Film, ExternalLink } from "lucide-react";

const GalleryGrid = ({ items }) => {
  const [selectedItem, setSelectedItem] = useState(null);
  const [filter, setFilter] = useState("all");

  const images = items.filter(item => item.media_type === "image");
  const videos = items.filter(item => item.media_type === "video");
  const filtered = filter === "image" ? images : filter === "video" ? videos : items;

  const filters = [
    { id: "all", label: "All", count: items.length },
    { id: "image", label: "Images", count: images.length, icon: ImageIcon },
    { id: "video", label: "Videos", count: videos.length, icon: Film },
  ];

  return (
    <div className="space-y-6">
      {/* Filter Bar */}
      {items.length > 0 && (
        <div className="flex items-center gap-3" data-testid="gallery-filters">
          {filters.map((f) => (
            <button
              key={f.id}
              onClick={() => setFilter(f.id)}
              className={`flex items-center gap-2 px-4 py-2 text-sm rounded-lg transition-all ${
                filter === f.id
                  ? "bg-yellow-500/20 text-yellow-500 border border-yellow-500/30"
                  : "bg-white/5 text-slate-400 border border-white/10 hover:bg-white/10 hover:text-white"
              }`}
              data-testid={`gallery-filter-${f.id}`}
            >
              {f.icon && <f.icon className="w-3.5 h-3.5" />}
              {f.label}
              <span className="text-xs opacity-60">({f.count})</span>
            </button>
          ))}
        </div>
      )}

      {/* Images Grid */}
      {filtered.filter(i => i.media_type === "image").length > 0 && (
        <div>
          {filter === "all" && <h4 className="text-xs font-mono tracking-[0.2em] uppercase text-amber-500 mb-4">Images</h4>}
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
            {filtered.filter(i => i.media_type === "image").map((item) => (
              <div
                key={item.id}
                className="group relative aspect-square overflow-hidden cursor-pointer rounded-lg border border-white/5 hover:border-yellow-500/30 transition-all"
                onClick={() => setSelectedItem(item)}
                data-testid={`gallery-image-${item.id}`}
              >
                <img
                  src={item.url}
                  alt={item.title}
                  className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                  loading="lazy"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                  <div className="absolute bottom-0 left-0 right-0 p-3">
                    <p className="text-white text-sm font-medium truncate">{item.title}</p>
                    {item.source && <p className="text-slate-400 text-xs mt-0.5">via {item.source}</p>}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Videos Grid */}
      {filtered.filter(i => i.media_type === "video").length > 0 && (
        <div>
          {filter === "all" && <h4 className="text-xs font-mono tracking-[0.2em] uppercase text-amber-500 mb-4 mt-6">Videos</h4>}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {filtered.filter(i => i.media_type === "video").map((item) => (
              <div
                key={item.id}
                className="group relative aspect-video overflow-hidden cursor-pointer rounded-lg border border-white/5 hover:border-yellow-500/30 transition-all"
                onClick={() => setSelectedItem(item)}
                data-testid={`gallery-video-${item.id}`}
              >
                <img
                  src={item.url.includes("youtube") 
                    ? `https://img.youtube.com/vi/${getYouTubeId(item.url)}/hqdefault.jpg`
                    : item.url
                  }
                  alt={item.title}
                  className="w-full h-full object-cover"
                  loading="lazy"
                />
                <div className="absolute inset-0 bg-black/40 group-hover:bg-black/30 transition-colors flex items-center justify-center">
                  <div className="w-14 h-14 rounded-full bg-yellow-500/90 flex items-center justify-center shadow-lg shadow-yellow-500/30 group-hover:scale-110 transition-transform">
                    <Play className="w-7 h-7 text-black ml-0.5" />
                  </div>
                </div>
                <div className="absolute bottom-0 left-0 right-0 p-4 bg-gradient-to-t from-black/90 to-transparent">
                  <p className="text-white text-sm font-semibold">{item.title}</p>
                  {item.description && <p className="text-slate-400 text-xs mt-1 line-clamp-1">{item.description}</p>}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Lightbox Dialog */}
      <Dialog open={!!selectedItem} onOpenChange={() => setSelectedItem(null)}>
        <DialogContent className="max-w-4xl bg-[#0B0F19] border-white/10 p-0 overflow-hidden">
          <DialogHeader className="p-4 border-b border-white/10 flex flex-row items-center justify-between">
            <div>
              <DialogTitle className="text-white font-['Outfit']">{selectedItem?.title}</DialogTitle>
              {selectedItem?.source && (
                <p className="text-xs text-slate-500 mt-1">Source: {selectedItem.source}</p>
              )}
            </div>
            {selectedItem?.source === "YouTube" && selectedItem?.url && (
              <a
                href={selectedItem.url}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-1.5 text-xs text-yellow-500 hover:text-yellow-400 transition-colors"
              >
                <ExternalLink className="w-3.5 h-3.5" />
                Open on YouTube
              </a>
            )}
          </DialogHeader>
          <div className="relative bg-black">
            {selectedItem?.media_type === "image" ? (
              <img
                src={selectedItem?.url}
                alt={selectedItem?.title}
                className="w-full max-h-[70vh] object-contain"
              />
            ) : selectedItem?.media_type === "video" && (
              <div className="aspect-video">
                {selectedItem?.url.includes("youtube") ? (
                  <iframe
                    src={`https://www.youtube.com/embed/${getYouTubeId(selectedItem?.url)}?autoplay=1`}
                    className="w-full h-full"
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                    allowFullScreen
                    title={selectedItem?.title}
                  />
                ) : (
                  <video src={selectedItem?.url} controls className="w-full h-full" />
                )}
              </div>
            )}
          </div>
          {selectedItem?.description && (
            <div className="p-4 border-t border-white/10">
              <p className="text-slate-300 text-sm">{selectedItem.description}</p>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {items.length === 0 && (
        <div className="text-center py-16">
          <ImageIcon className="w-12 h-12 text-slate-600 mx-auto mb-4" />
          <p className="text-slate-500 text-sm">No gallery items yet.</p>
          <p className="text-slate-600 text-xs mt-1">Admins can add images and videos from the admin panel.</p>
        </div>
      )}
    </div>
  );
};

const getYouTubeId = (url) => {
  const match = url?.match(/(?:youtube\.com\/(?:[^\/]+\/.+\/|(?:v|e(?:mbed)?)\/|.*[?&]v=)|youtu\.be\/)([^"&?\/\s]{11})/);
  return match ? match[1] : "";
};

export default GalleryGrid;
